import { getBootFlag, BootFlags } from '@/app-config';

/* Flags that are requested via the featureFlags API, must be allow-listed prior to use.
 * Flag access is controlled via the `authorized-flag-access-v2` Launch Darkly flag.
 * To add an authorized flag, tag the feature flag with the `authorized-ui-flag-access` tag.
 * A script will run regularly to update the `authorized-flag-access-v2` variation with
 * all flags tagged with `authorized-ui-flag-access`.
 *
 * Alternatively, you can trigger this script manually by calling `droidcli launch-darkly update-ui-access-flag --apply`
 */
// These are flags we want to keep around because they control critical aspects of the application
const PERMANANT_FLAGS = {
  clientSideTracing: {
    key: 'client-side-tracing',
    value: false,
  },
};

// These flags are primarily used for sales team enablement
const DEMO_FLAGS = {};

// Semi-permanent flags that control product-level features (events, traces, etc)
const ENTITLEMENT_FLAGS = {
  chronosphereUniversityLinkEnabled: {
    key: 'chronosphere-university-link-enabled',
    value: false,
  },
  docsSiteMode: {
    key: 'docs-site-mode',
    value: 'disabled',
  },
  enableSCIM: {
    key: 'enable-scim',
    value: false,
  },
  enableTimelineUI: {
    key: 'enable-timeline-ui',
    value: false,
  },
  loggingUI: {
    key: 'logging-ui',
    value: false,
  },
  // Below flag is deprecated. After transferring the usage, this flag can be removed.
  enableAlertBacktesting: {
    key: 'enable-alert-backtesting',
    value: false,
  },
  enableAlertBacktestingUI: {
    key: 'enable-alert-backtesting-ui',
    value: false,
  },
};

// There features span across the entire app
const GLOBAL_FEATURE_FLAGS = {
  enableRemoveContainerOverflow: {
    key: 'enable-remove-container-overflow',
    value: false,
  },
  uiHeartbeatCheckIntervalSeconds: {
    key: 'ui-hearbeat-check-interval-seconds',
    value: 0,
  },
};

// DRI: Error Budgets
const SERVICE_WIDGET_FLAGS = {
  showServiceInfoWidget: {
    key: 'show-service-info-widget',
    value: false,
  },
};

// DRI: Responder
const SERVICE_FLAGS = {
  serviceConfigv2: {
    key: 'service-config-v2',
    value: false,
  },
  metricsExplorerPinnedScopeEnabled: {
    key: 'pinned-scope.native-metrics-explorer',
    value: 'disabled',
  },
  metricsDdxPoc: {
    key: 'metrics-ddx-poc',
    value: false,
  },
  enableXrayPreview: {
    key: 'enable-xray-preview',
    value: false,
  },
};

// DRI: Control
const AGG_RULES_FLAGS = {
  aggRulesRework: {
    key: 'agg-rules-rework',
    value: false,
  },
};

// DRI: Control
const METRICS_ANALYZER_FLAGS = {
  uiMetricsAnalyzerIngestionSamplingRate: {
    key: 'ui-metrics-analyzer-ingestion-sampling-rate',
    value: 100,
  },
  uiMetricsAnalyzerMatchingNamespace: {
    key: 'ui-metrics-analyzer-matching-namespace',
    value: false,
  },
};

// DRI: Control
const METRICS_USAGE_FLAGS = {
  usageDetailsExecutionTruncationUI: {
    key: 'usage-details-execution-truncation-ui',
    value: false,
  },
};

// DRI: Control
const METRICS_ALLOCATION_FLAGS = {
  metricsQuotasRework: {
    key: 'metrics-quotas-rework',
    value: false,
  },
  disableQuotaConfiguration: {
    key: 'disable-quota-configuration',
    value: false,
  },
};

// DRI: Control
const CONSUMPTION_BASED_PRICING_FLAGS = {
  consumptionLicenseOverviewUi: {
    key: 'consumption-license-overview-ui',
    value: false,
  },
  enableUiConsumptionDatasource: {
    key: 'enable-ui-consumption-datasource',
    value: false,
  },
  enableLogsConsumptionUI: {
    key: 'enable-logs-consumption-ui',
    value: false,
  },
  enableMetricsConsumptionUI: {
    key: 'enable-metrics-consumption-ui',
    value: false,
  },
  tracingConsumptionPricing: {
    key: 'tracing-consumption-pricing',
    value: false,
  },
  tracingConsumptionLicenseContractEpoch: {
    key: 'tracing-consumption-license-contract-epoch',
    value: 0,
  },
};

// DRI: Control
const LICENSE_OVERVIEW_FLAGS = {
  enableLicenseOverview: {
    key: 'enable-license-overview',
    value: false,
  },
  enableLicenseOverviewAdminHomepage: {
    key: 'enable-license-overview-admin-homepage',
    value: false,
  },
};

// DRI: Control
const RECOMMENDATIONS_FLAGS = {
  enableControlRecommendationsUI: {
    key: 'enable-control-recommendations-ui',
    value: false,
  },
};

// DRI: Alerting
const MONITORS_FLAGS = {
  enableNotificationRoutesGroupBy: {
    key: 'enable-notification-routes-group-by',
    value: false,
  },
  enableSeriesLabelRouting: {
    key: 'enable-series-label-routing',
    value: false,
  },
  monitorEntityLayoutUpdateUi: {
    key: 'monitor-entity-layout-update-ui',
    value: false,
  },
  enableLegacyTimelineEventPresets: {
    key: 'enable-legacy-timeline-event-presets',
    value: false,
  },
};

// DRI: Alerting
const ALERTING_FLAGS = {
  enableAlertsResolutionNotes: {
    key: 'enable-alerts-resolution-notes',
    value: false,
  },
};

// DRI: Alerting
const MUTING_RULES_FLAGS = {
  enableMutingrulesEnhancementsUI: {
    key: 'enable-mutingrules-enhancements-ui',
    value: false,
  },
  enableDdxSuggestions: {
    key: 'enable-ddx-suggestions',
    value: false,
  },
};

// DRI: Dev Ergo
const DEV_ERGO_FLAGS = {
  devErgoBrowserMonitoring: {
    key: 'dev-ergo-browser-monitoring',
    value: false,
  },
  devErgoCleanupLayout: {
    key: 'dev-ergo-cleanup-layout',
    value: false,
  },
  devErgoCommandCenter: {
    key: 'dev-ergo-command-center',
    value: false,
  },
  devErgoExternalPanel: {
    key: 'dev-ergo-external-panel',
    value: false,
  },
  devErgoHomeActivityFeed: {
    key: 'dev-ergo-home-activity-feed',
    value: false,
  },
  devErgoHomepageMostViewed: {
    key: 'dev-ergo-home-trending',
    value: false,
  },
  devErgoPanelGroupBuilder: {
    key: 'dev-ergo-panel-group-builder',
    value: false,
  },
  devErgoStatusHistory: {
    key: 'dev-ergo-status-history',
    value: false,
  },
  devErgoTabbedLayout: {
    key: 'dev-ergo-tabbed-layout',
    value: false,
  },
  devErgoTriageMapRefactor: {
    key: 'dev-ergo-triage-map-perf-boost',
    value: false,
  },
  devErgoVariableRegexKillSwitch: {
    key: 'dev-ergo-variable-regex-kill-switch',
    value: false,
  },
  enableDashboardJsonDialogSort: {
    key: 'enable-dashboard-json-dialog-sort',
    value: false,
  },
  enableDashboardPanelViewQueriesPopover: {
    key: 'enable-dashboard-panel-view-queries-popover',
    value: false,
  },
  enableDashboardVariableBulkManagement: {
    key: 'enable-dashboard-variable-bulk-management',
    value: false,
  },
  enableDataQueriesLoadingCacheCheck: {
    key: 'enable-data-queries-loading-cache-check',
    value: false,
  },
  enableEntityPopularityUi: {
    key: 'enable-entity-popularity-ui',
    value: false,
  },
  enableHeatmapPanelEvents: {
    key: 'enable-heatmap-panel-events',
    value: false,
  },
  enableIntervalVariableAutoSizeOption: {
    key: 'enable-interval-variable-auto-size-option',
    value: false,
  },
  enableTimeSeriesChartDeclutterCommonLabels: {
    key: 'enable-time-series-chart-declutter-common-labels',
    value: false,
  },
  enableTopListBarChartRewrite: {
    key: 'enable-top-list-bar-chart-rewrite',
    value: false,
  },
  enableVersionHistoryAiExplanation: {
    key: 'enable-version-history-ai-explanation',
    value: false,
  },
};

// DRI: Dashboarding
const DASHBOARDING_FLAGS = {
  enableDefaultNativeDashboardPreview: {
    key: 'enable-default-native-dashboard-preview',
    value: false,
  },
  enableDashboardCompareMode: {
    key: 'enable-dashboard-compare-mode',
    value: false,
  },
  enableNativeDashboards: {
    key: 'enable-native-dashboards',
    value: 'disabled',
  },
  enableVizFrameworkFormulas: {
    key: 'enable-vf-formulas',
    value: false,
  },
  nativeDashboardsDatapointsLimit: {
    key: 'native-dashboards-datapoints-limit',
    value: 400000,
  },
  nativeDashboardsSeriesLimit: {
    key: 'native-dashboards-series-limit',
    value: 4000,
  },
  showNativeDashboardPreview: {
    key: 'show-native-dashboard-preview',
    value: false,
  },
  enableLoadClassicDashboardButton: {
    key: 'enable-load-classic-dashboard-button',
    value: true,
  },
  enableNativeMetricsExplorerMode: {
    key: 'enable-native-metrics-explorer-mode',
    value: 'off',
  },
  enableNativeDashboardPinnedScopes: {
    key: 'pinned-scope.native-dashboards',
    value: false,
  },
  maxDatapointsPerSeries: {
    key: 'max-datapoints-per-series',
    value: 1920,
  },
  enableExperimentalResolutionControls: {
    key: 'enable-experimental-resolution-controls',
    value: false,
  },
  enableNewVariableUx: {
    key: 'enable-new-variable-ux',
    value: false,
  },
  enableFormatPromQL: {
    key: 'enable-format-promql',
    value: false,
  },
  enablePromQLQueryBuilder: {
    key: 'enable-promql-query-builder',
    value: false,
  },
  enablePersonalCollections: {
    key: 'enable-personal-collections',
    value: false,
  },
  enableDashboardSavedViews: {
    key: 'enable-dashboard-saved-views',
    value: false,
  },
  enableDashboardAnnotations: {
    key: 'enable-dashboard-annotations',
    value: false,
  },
  enableDistributionChartPlugin: {
    key: 'enable-distribution-chart-plugin',
    value: false,
  },
  enableAutoStackedBarTruncation: {
    key: 'enable-auto-stacked-bar-truncation',
    value: false,
  },
  enableDashboardUndoNotification: {
    key: 'enable-dashboard-undo-notification',
    value: false,
  },
  enableMutableDashboardAnnotations: {
    key: 'enable-mutable-dashboard-annotations',
    value: false,
  },
};

// DRI: Tracing
const TRACING_FLAGS = {
  tracingTopologyViewEdges: {
    key: 'tracing-topology-view-edges',
    value: false,
  },
  tracingDdxSampleCount: {
    key: 'tracing-ddx-sample-count',
    value: 16,
  },
  tracingSystemLimitMetric: {
    key: 'tracing-system-limit-metric',
    value: 'off',
  },
  tracingTagPartitionConcurrency: {
    key: 'tracing-tag-query-partition-concurrency',
    value: 1,
  },
};

// DRI: Impact
const IMPACT_FLAGS = {
  derivedLabelsCrud: {
    key: 'derived-labels-crud',
    value: false,
  },
  enableRelatedAlertsSummaryViz: {
    key: 'enable-related-alerts-summary-viz',
    value: false,
  },
  impactAnalyzeAlertsOnAlertsPage: {
    key: 'impact-analyze-alerts-on-alerts-page',
    value: false,
  },
  alertAnalysisMaxTimeRangeDuration: {
    key: 'alert-analysis-max-time-range-duration',
    value: 3600, // in seconds
  },
  impactAtMentions: {
    key: 'impact-at-mentions',
    value: false,
  },
  impactHomeCustomization: {
    key: 'impact-home-customization',
    value: false,
  },
};

// DRI: Alerting
const TIMELINE_FLAGS = {
  timelineEnableRecentFilters: {
    key: 'enable-recent-filters',
    value: false,
  },
  timelineEnableSavedFilters: {
    key: 'enable-saved-filters',
    value: false,
  },
  timelineEnableSharedFilters: {
    key: 'enable-shared-filters',
    value: false,
  },
  timelineEnableLabelColumns: {
    key: 'enable-event-label-columns',
    value: true,
  },
  enableChangeEventsUi: {
    key: 'enable-change-events-ui',
    value: 'disabled',
  },
};

// DRI: Logging
const LOGGING_FLAGS = {
  loggingInternalNavEnabled: {
    key: 'logging-internal-nav-enabled',
    value: false,
  },
  loggingEnableLoggingDashboardDatasource: {
    key: 'enable-logging-dashboard-datasource',
    value: false,
  },
  loggingEnableProgressiveDatasource: {
    key: 'logging-enable-progressive-datasource',
    value: false,
  },
  loggingEnableUsageAnalysis: {
    key: 'enable-usage-analysis',
    value: false,
  },
  loggingEnableLicenseOverview: {
    key: 'logging-enable-license-overview',
    value: false,
  },
  loggingEnableLogPinning: {
    key: 'logging-enable-log-pinning',
    value: false,
  },
  loggingEnableParsers: {
    key: 'logging-enable-parsers',
    value: false,
  },
  loggingEnableControlRules: {
    key: 'logging-enable-control-rules',
    value: false,
  },
  enableLogProcessedBytesLicenseDashboard: {
    key: 'enable-log-processed-bytes-license-dashboard',
    value: false,
  },
  enableLogsLookupTables: {
    key: 'enable-logs-lookup-tables',
    value: false,
  },
  enableAiLogSummary: {
    key: 'enable-ai-log-summary',
    value: false,
  },
  enableFieldMappingsIngestConfigUi: {
    key: 'enable-field-mappings-ingest-config-ui',
    value: false,
  },
  enableLogsIngestConsolidatedView: {
    key: 'enable-logs-ingest-consolidated-view',
    value: false,
  },
};

// DRI: natural-language-interface
const NATURAL_LANGUAGE_INTERFACE_FLAGS = {
  enableMetricsCatalogDrawer: {
    key: 'enable-metrics-catalog-drawer',
    value: false,
  },
  aiDescriptionsEnabled: {
    key: 'ai-descriptions-enabled-ui',
    value: false,
  },
  aiPromQLQueryGeneration: {
    key: 'ai-promql-query-generation',
    value: false,
  },
  aiChatUi: {
    key: 'ai-chat-ui',
    value: false,
  },
  // Dashboards
  dashboardsEnableAiDescription: {
    key: 'dashboards.enable-ai-description',
    value: false,
  },
  enableAiLogsQueryGeneration: {
    key: 'enable-ai-logs-query-generation',
    value: false,
  },
  enableAiQueryLanguageErrors: {
    key: 'enable-ai-query-language-errors',
    value: false,
  },
};

const SERVICE_ACCOUNT_FLAGS = {
  // DRI: zach@
  enableServiceTokenUsageCustomerUi: {
    key: 'enable-service-token-usage-customer-ui',
    value: false,
  },
};

// DRI: Viz Framework
const VIZ_FRAMEWORK_FLAGS = {
  enableVizFrameworkMocks: {
    key: 'enable-viz-framework-mocks',
    value: false,
  },
  enableTimeRangeProviderV2: {
    key: 'enable-time-range-provider-v2',
    value: false,
  },
  enableNewDateTimePickerFormat: {
    key: 'enable-new-date-time-picker-format',
    value: false,
  },
  panwRebrandStubYear: {
    key: 'panw-rebrand-stub-year',
    value: getBootFlag(BootFlags.PANW_REBRAND_STUB_YEAR),
  },
  enableNewLogoStubYear: {
    key: 'enable-new-logo-stub-year',
    value: getBootFlag(BootFlags.ENABLE_NEW_LOGO_STUB_YEAR),
  },
};

const DEVEX_FLAGS = {
  enableSidebar: {
    key: 'enable-sidebar',
    value: false,
  },
};

const AI_KG_FLAGS = {
  showInferredAlertEvents: {
    key: 'inferred-alert-events',
    value: false,
  },
  metricExplorerAnomalies: {
    key: 'metric-explorer-anomalies',
    value: false,
  },
  agentInvestigationsEnabled: {
    key: 'agent-investigations-enabled',
    value: false,
  },
  enableSystemLibrary: {
    key: 'enable-system-library',
    value: false,
  },
  enableChronoctlBuilder: {
    key: 'chronoctl-builder',
    value: false,
  },
};

const EXPLORATION_FLAGS = {
  enableNotebooks: {
    key: 'enable-notebooks',
    value: false,
  },
  enableNotebookInvestigations: {
    key: 'enable-notebook-investigations',
    value: false,
  },
  enableNotebooksLocalCollab: {
    key: 'enable-notebooks-local-collab',
    value: false,
  },
};

/**
 * Configuration for all feature flags fetched from the GQL API.
 */
export const FEATURE_FLAG_CONFIG = {
  ...PERMANANT_FLAGS,
  ...DEMO_FLAGS,
  ...GLOBAL_FEATURE_FLAGS,
  ...ENTITLEMENT_FLAGS,
  /*** Workstream flags ***/
  ...ALERTING_FLAGS,
  ...SERVICE_WIDGET_FLAGS,
  ...SERVICE_FLAGS,
  ...METRICS_ANALYZER_FLAGS,
  ...METRICS_USAGE_FLAGS,
  ...METRICS_ALLOCATION_FLAGS,
  ...CONSUMPTION_BASED_PRICING_FLAGS,
  ...LICENSE_OVERVIEW_FLAGS,
  ...RECOMMENDATIONS_FLAGS,
  ...MONITORS_FLAGS,
  ...DEV_ERGO_FLAGS,
  ...DASHBOARDING_FLAGS,
  ...TRACING_FLAGS,
  ...TIMELINE_FLAGS,
  ...LOGGING_FLAGS,
  ...AGG_RULES_FLAGS,
  ...SERVICE_ACCOUNT_FLAGS,
  ...VIZ_FRAMEWORK_FLAGS,
  ...IMPACT_FLAGS,
  ...NATURAL_LANGUAGE_INTERFACE_FLAGS,
  ...MUTING_RULES_FLAGS,
  ...DEVEX_FLAGS,
  ...AI_KG_FLAGS,
  ...EXPLORATION_FLAGS,
};

/**
 * Type representing the overall config object for all feature flags fetched from the GQL API.
 */
export type FeatureFlagConfig = typeof FEATURE_FLAG_CONFIG;

/**
 * These flags are local to the client and are not controlled by LaunchDarkly
 * They are used to control features that are not yet ready for production.
 * All tenants will have the same value for these flags
 * eg. This config is the source of truth for the flag
 */
export const LOCAL_FEATURE_FLAGS = {
  enableVizFrameworkSchemaValidation: false,
  shouldAutoMigrateGrafanaDashboard: false,
  enableDashboardSafeMode: false,
  truncationStrategyLimit: 150,
  enableCopyPanelAsJson: true,
  aiLogsQueryGeneration: false,
  aiToolsKnowledgeBuilderQueryEnabled: false,
};

/**
 * Feature flags that are local to the client (i.e. not backed by a value on the server from Launch Darkly).
 */
export type LocalFeatureFlags = typeof LOCAL_FEATURE_FLAGS;

/**
 * Returns true if the flag is a local feature flag
 */
export const isLocalFeatureFlag = (flag: string) => flag in LOCAL_FEATURE_FLAGS;
